import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { adminApi, type AdminUser } from "./api";

const adminTokenKey = "ai-crm:admin-token";

interface AuthContextValue {
  token: string | null;
  admin: AdminUser | null;
  isReady: boolean;
  login: (token: string, admin: AdminUser) => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = React.useState<string | null>(null);
  const [admin, setAdmin] = React.useState<AdminUser | null>(null);
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    const savedToken = window.localStorage.getItem(adminTokenKey);
    if (!savedToken) {
      setIsReady(true);
      return;
    }

    adminApi
      .me(savedToken)
      .then((me) => {
        setToken(savedToken);
        setAdmin(me);
      })
      .catch(() => {
        window.localStorage.removeItem(adminTokenKey);
      })
      .finally(() => setIsReady(true));
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      token,
      admin,
      isReady,
      login: (nextToken, nextAdmin) => {
        queryClient.clear();
        window.localStorage.setItem(adminTokenKey, nextToken);
        setToken(nextToken);
        setAdmin(nextAdmin);
      },
      logout: () => {
        queryClient.clear();
        window.localStorage.removeItem(adminTokenKey);
        setToken(null);
        setAdmin(null);
      },
    }),
    [admin, isReady, queryClient, token],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const value = React.useContext(AuthContext);
  if (!value) throw new Error("useAdminAuth must be used inside AuthProvider");
  return value;
}
